import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AxiosError } from "axios";
import { authService } from "../services/authService";
import type {
  AuthState,
  LoginCredentials,
  Permission,
} from "../types/auth.types";
import type { ApiErrorResponse } from "../types/api.types";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthState["user"]) => void;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  hasModule: (moduleName: string) => boolean;
  getUserPermissions: () => Permission[];
  getPermissionsByModule: () => { module: string; permissions: Permission[] }[];
}

const unauthenticatedState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    ...unauthenticatedState,
    loading: true,
  });

  const hasPermission = (permissionCode: string): boolean => {
    if (!state.user) return false;
    return state.user.permissions?.some((permission) => permission.code === permissionCode) || false;
  };

  const hasAnyPermission = (permissionCodes: string[]): boolean =>
    permissionCodes.length > 0 && permissionCodes.some(hasPermission);

  const hasAllPermissions = (permissionCodes: string[]): boolean =>
    permissionCodes.length > 0 && permissionCodes.every(hasPermission);

  const hasModule = (moduleName: string): boolean => {
    if (!state.user) return false;
    return state.user.permissions?.some((permission) => permission.module === moduleName) || false;
  };

  const getUserPermissions = (): Permission[] => state.user?.permissions || [];

  const getPermissionsByModule = () => {
    const grouped = getUserPermissions().reduce<Record<string, Permission[]>>(
      (result, permission) => {
        if (!result[permission.module]) result[permission.module] = [];
        result[permission.module].push(permission);
        return result;
      },
      {},
    );

    return Object.entries(grouped).map(([module, permissions]) => ({
      module,
      permissions,
    }));
  };

  const setUser = (user: AuthState["user"]) => {
    setState((previous) => ({
      ...previous,
      user,
      isAuthenticated: Boolean(user),
    }));
  };

  const login = async (credentials: LoginCredentials) => {
    setState((previous) => ({ ...previous, loading: true, error: null }));

    try {
      const response = await authService.login(credentials);
      const { user, redirect_url: redirectUrl } = response.data.data;

      if (user.type === "client") {
        setState(unauthenticatedState);
        if (redirectUrl) window.location.assign(redirectUrl);
        return;
      }

      if (user.type !== "admin") {
        throw new Error("El usuario no tiene acceso al panel administrativo");
      }

      setState({
        user,
        isAuthenticated: true,
        loading: false,
        error: null,
      });
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      setState({
        ...unauthenticatedState,
        error:
          axiosError.response?.data?.message ||
          (error instanceof Error ? error.message : "Error al iniciar sesión"),
      });
      throw error;
    }
  };

  const logout = async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      await authService.logout();
    } finally {
      setState(unauthenticatedState);
    }
  };

  useEffect(() => {
    const handleUnauthorized = () => setState(unauthenticatedState);
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await authService.me();
        const user = response.data.data;

        if (user.type !== "admin") {
          setState(unauthenticatedState);
          return;
        }

        setState({
          user,
          isAuthenticated: true,
          loading: false,
          error: null,
        });
      } catch {
        setState(unauthenticatedState);
      }
    };

    void restoreSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        setUser,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasModule,
        getUserPermissions,
        getPermissionsByModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
